import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { BaseApiService } from '@app/core/api/base-api.service';
import {
  AgentDto,
  AgentInputDto,
  AgentInstructionDto,
  AgentPromptDto,
  AgentStatusType,
  AgentVersionDto,
  AgentVersionHistoryDto,
  CreateAgentPayload,
  CreateUpdateAgentInputPayload,
  CreateUpdateAgentInstructionPayload,
  CreateUpdateAgentPromptPayload,
  UpdateAgentPayload,
} from '@stores/agents';

@Injectable({ providedIn: 'root' })
export class AgentService {
  private readonly api = inject(BaseApiService);
  private readonly apiVersion = 'v1';
  private readonly resourcePath = `${this.apiVersion}/tenants`;

  getAgents(tenantUuid: string, page?: number, size?: number): Observable<AgentDto[]> {
    type ApiAgent = AgentDto;
    let params = new HttpParams();
    if (page && size) {
      params.set('page', String(page)).set('size', String(size));
    }
    return this.api
      .get<readonly ApiAgent[]>(`${this.resourcePath}/${tenantUuid}/agents`, { params })
      .pipe(map((arr) => (Array.isArray(arr) ? arr : [])));
  }

  getAgentHubs(tenantUuid: string): Observable<string[]> {
    type ApiAgentHub = string;
    return this.api
      .get<readonly ApiAgentHub[]>(`${this.resourcePath}/${tenantUuid}/agents/agentHubs`)
      .pipe(map((arr) => (Array.isArray(arr) ? arr : [])));
  }

  getAgentDetail(tenantUuid: string, agentUuid: string): Observable<AgentDto> {
    type ApiAgent = AgentDto;
    return this.api.get<ApiAgent>(`${this.resourcePath}/${tenantUuid}/agents/${agentUuid}`);
  }

  getAgentInstruction(tenantUuid: string, agentUuid: string): Observable<AgentInstructionDto[]> {
    return this.api.get<AgentInstructionDto[]>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/instructions`,
    );
  }

  getAgentPrompts(tenantUuid: string, agentUuid: string): Observable<AgentPromptDto[]> {
    return this.api.get<AgentPromptDto[]>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/prompts`,
    );
  }

  getAgentVersions(tenantUuid: string, agentUuid: string): Observable<AgentVersionHistoryDto[]> {
    return this.api.get<AgentVersionHistoryDto[]>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/versions/history`,
    );
  }

  createAgentVersion(tenantUuid: string, agentUuid: string): Observable<Partial<AgentVersionDto>> {
    return this.api.post<Partial<AgentVersionDto>>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/versions`,
      {},
    );
  }

  updateAgentVersionStatus(
    tenantUuid: string,
    agentUuid: string,
    versionId: number,
    status: AgentStatusType,
  ) {
    return this.api.put<Partial<AgentVersionDto>>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/versions/${versionId}/status`,
      {
        status,
      },
    );
  }

  getAgentInputs(tenantUuid: string, agentUuid: string): Observable<AgentInputDto[]> {
    return this.api.get<AgentInputDto[]>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/inputs`,
    );
  }

  createAgentInputs(
    tenantUuid: string,
    agentUuid: string,
    payload: CreateUpdateAgentInputPayload[],
  ) {
    return this.api
      .post<readonly AgentInputDto[]>(
        `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/inputs/assign`,
        payload,
      )
      .pipe(map((arr) => (Array.isArray(arr) ? arr : [])));
  }

  updateAgentInput(
    tenantUuid: string,
    agentUuid: string,
    agentInputId: number,
    payload: CreateUpdateAgentInputPayload,
  ) {
    return this.api.put<AgentInputDto>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/inputs/${agentInputId}`,
      payload,
    );
  }

  deleteAgentInput(
    tenantUuid: string,
    agentUuid: string,
    agentInputId: number,
  ): Observable<AgentPromptDto> {
    return this.api.delete(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/inputs/${agentInputId}`,
    );
  }

  createAgentPrompts(
    tenantUuid: string,
    agentUuid: string,
    payload: CreateUpdateAgentPromptPayload,
  ): Observable<AgentPromptDto> {
    return this.api.post<AgentPromptDto>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/prompts`,
      payload,
    );
  }

  updateAgentPrompts(
    tenantUuid: string,
    agentUuid: string,
    agentPromptId: string,
    payload: CreateUpdateAgentPromptPayload,
  ): Observable<AgentPromptDto> {
    return this.api.put<AgentPromptDto>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/prompts/${agentPromptId}`,
      payload,
    );
  }

  updateAgentInstruction(
    tenantUuid: string,
    agentUuid: string,
    agentInstructionId: number,
    payload: CreateUpdateAgentInstructionPayload,
  ): Observable<AgentInstructionDto> {
    return this.api.put<AgentInstructionDto>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/instructions/${agentInstructionId}`,
      payload,
    );
  }

  createAgentInstruction(
    tenantUuid: string,
    agentUuid: string,
    payload: CreateUpdateAgentInstructionPayload,
  ): Observable<AgentInstructionDto> {
    return this.api.post<AgentInstructionDto>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/instructions`,
      payload,
    );
  }

  confirmAgentInstructionFile(tenantUuid: string, agentUuid: string, agentInstructionId: number) {
    return this.api.post(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/instructions/confirm-file/${agentInstructionId}`,
      {},
    );
  }

  deleteAgentInstruction(tenantUuid: string, agentUuid: string, agentInstructionId: number) {
    return this.api.delete(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/instructions/${agentInstructionId}`,
    );
  }

  deleteAgentPrompt(tenantUuid: string, agentUuid: string, agentPromptId: number) {
    return this.api.delete(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}/prompts/${agentPromptId}`,
    );
  }

  createAgent(tenantUuid: string, payload: CreateAgentPayload): Observable<AgentDto> {
    type ApiAgent = AgentDto;
    return this.api.post<ApiAgent>(`${this.resourcePath}/${tenantUuid}/agents`, payload);
  }

  updateAgent(
    tenantUuid: string,
    agentUuid: string,
    payload: UpdateAgentPayload,
  ): Observable<AgentDto> {
    type ApiAgent = AgentDto;
    return this.api.put<ApiAgent>(
      `${this.resourcePath}/${tenantUuid}/agents/${agentUuid}`,
      payload,
    );
  }

  deleteAgent(tenantUuid: string, agentUuid: string): Observable<void> {
    return this.api.delete(`${this.resourcePath}/${tenantUuid}/agents/${agentUuid}`);
  }
}
